import { useEffect, useState } from "react";
import api from "../axios";
import { Package, Plus, Trash2, Pencil, ImageOff, X } from "lucide-react";

const MEDIA_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(
  /\/api\/?$/,
  ""
);

const emptyForm = {
  id: null,
  name: "",
  description: "",
  sale_price: "",
  floor_price: "",
  status: "active",
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/products");
      setProducts(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setFile(null);
    setFilePreview(null);
    setEditingId(null);
    setError("");
  };

  const startEdit = (p) => {
    setForm({
      id: p.id,
      name: p.name,
      description: p.description || "",
      sale_price: p.sale_price,
      floor_price: p.floor_price,
      status: p.status,
    });
    setEditingId(p.id);
    setFile(null);
    setFilePreview(p.image_url ? `${MEDIA_BASE}${p.image_url}` : null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setFilePreview(f ? URL.createObjectURL(f) : filePreview);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) return setError("Product/Service ka naam likhna zaroori hai.");
    if (!form.sale_price) return setError("Sale price likhna zaroori hai.");
    if (!form.floor_price) return setError("Floor (minimum) price likhna zaroori hai.");
    if (Number(form.floor_price) > Number(form.sale_price)) {
      return setError("Floor price, Sale price se zyada nahi ho sakti.");
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("description", form.description);
      fd.append("sale_price", form.sale_price);
      fd.append("floor_price", form.floor_price);
      fd.append("status", form.status);
      if (file) fd.append("image", file);

      if (editingId) {
        await api.put(`/products/${editingId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/products", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      resetForm();
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.message || "Save karne me error aayi.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (p) => {
    try {
      await api.put(`/products/${p.id}`, {
        status: p.status === "active" ? "inactive" : "active",
      });
      loadProducts();
    } catch (e) {
      alert("Update failed");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Ye product/service delete karna hai?")) return;
    try {
      await api.delete(`/products/${id}`);
      loadProducts();
    } catch (e) {
      alert("Delete failed");
    }
  };

  return (
    <div className="w-full min-h-full bg-gray-50 px-4 py-6 md:px-8 md:py-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="text-purple-600" size={24} />
            Products & Services
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Yahan apne products/services, unki images, sale price aur minimum (floor) price add karein — AI WhatsApp par chat karte waqt isi catalog se negotiate karega.
          </p>
        </div>

        {/* Add / Edit form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-gray-800">
              {editingId ? "Product/Service Edit Karein" : "Naya Product/Service Add Karein"}
            </h2>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs text-gray-500 flex items-center gap-1 hover:text-gray-800"
              >
                <X size={14} /> Cancel Edit
              </button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. GFC Hair Treatment, IVF Consultation"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">Description</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description jo AI ko customer se baat karte waqt pata honi chahiye"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">
                Sale Price (₹) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.sale_price}
                onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                placeholder="Jo price customer ko pehle offer hogi"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">
                Floor Price (₹) — Minimum <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.floor_price}
                onChange={(e) => setForm({ ...form, floor_price: e.target.value })}
                placeholder="Isse kam pe kabhi nahi bikega, customer ko kabhi nahi dikhega"
                className="border border-gray-300 rounded-lg px-3 py-2.5 w-full mt-1.5 focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-gray-700">Image (optional)</label>
              <div className="flex items-center gap-4 mt-1.5">
                <label className="flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer text-sm text-gray-600 hover:border-purple-400 hover:text-purple-600 transition">
                  <Plus size={16} />
                  {file ? file.name : "Image upload karein"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {filePreview && (
                  <img src={filePreview} alt="preview" className="w-16 h-16 rounded-lg object-cover border" />
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold px-6 py-2.5 rounded-lg shadow hover:opacity-90 disabled:opacity-50"
          >
            <Plus size={16} /> {saving ? "Saving..." : editingId ? "Update Product" : "Add Product"}
          </button>
        </form>

        {/* List */}
        <h2 className="text-lg font-bold text-gray-900 mb-4">Sabhi Products / Services</h2>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : !products.length ? (
          <p className="text-gray-500 text-sm">Abhi tak koi product/service add nahi kiya gaya.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 pb-8">
            {products.map((p) => (
              <div key={p.id} className="bg-white border rounded-2xl shadow-sm p-4 flex gap-4">
                {p.image_url ? (
                  <img
                    src={`${MEDIA_BASE}${p.image_url}`}
                    alt={p.name}
                    className="w-20 h-20 rounded-lg object-cover border flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <ImageOff size={20} className="text-gray-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{p.name}</span>
                    <span
                      className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                        p.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {p.status}
                    </span>
                  </div>
                  {p.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{p.description}</p>
                  )}
                  <div className="text-sm mt-2 flex items-center gap-3">
                    <span className="font-bold text-gray-900">₹{p.sale_price}</span>
                    <span className="text-xs text-gray-400">Floor: ₹{p.floor_price}</span>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => startEdit(p)}
                      className="text-xs text-blue-600 border border-blue-200 px-2.5 py-1 rounded-lg flex items-center gap-1"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => toggleStatus(p)}
                      className="text-xs text-gray-600 border px-2.5 py-1 rounded-lg"
                    >
                      {p.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      onClick={() => deleteProduct(p.id)}
                      className="text-xs text-red-600 border border-red-200 px-2.5 py-1 rounded-lg"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}